import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { learnerConsultationApi, type LearnerConsultationBody } from '@/api/learnerConsultations'
import { getApiErrorMessage } from '@/utils/apiError'

export const learnerConsultationKeys = {
  list: (userId: number) => ['lc', 'admin', 'learner', userId, 'consultations'] as const,
}

export function useLearnerConsultations(userId: number) {
  return useQuery({
    queryKey: learnerConsultationKeys.list(userId),
    queryFn: async () => (await learnerConsultationApi.list(userId)).data.data ?? [],
    enabled: Number.isFinite(userId) && userId > 0,
  })
}

export function useSaveLearnerConsultation(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: ({ consultationId, body }: { consultationId: number | null; body: LearnerConsultationBody }) =>
      consultationId == null
        ? learnerConsultationApi.create(userId, body)
        : learnerConsultationApi.update(consultationId, body),
    onSuccess: (_res, { consultationId }) => {
      qc.invalidateQueries({ queryKey: learnerConsultationKeys.list(userId) })
      toast.success(t(consultationId == null
        ? 'admin:students.consultation.toast.created'
        : 'admin:students.consultation.toast.updated'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}

export function useDeleteLearnerConsultation(userId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation(['admin', 'common'])
  return useMutation({
    mutationFn: (consultationId: number) => learnerConsultationApi.remove(consultationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: learnerConsultationKeys.list(userId) })
      toast.success(t('admin:students.consultation.toast.deleted'))
    },
    onError: (err) => toast.error(getApiErrorMessage(err, t)),
  })
}
